/**
 * usePersonProfilePath — scope-aware Person profile URL builder.
 *
 * Person records are tenant-scoped data (see docs/plan/SCOPE_IN_URL.md
 * §4.7), but operators opening a person profile from inside a location
 * surface should stay in the location chrome — flipping the app to
 * /company/persons drops the location rail and (per pendings #5) was
 * also a cross-location info-disclosure risk.
 *
 * The hook reads `:locationId` from the URL and routes to the
 * location-scoped wrapper (gated by ModulePageGuard) when we're in
 * location chrome, falling back to the legacy /persons/* URL when
 * we're not (company surfaces, /persons/me, etc.).
 *
 * Returned builder accepts an optional `tab` so existing call sites
 * like `?tab=guest` / `?tab=staff` keep working — the base path also
 * follows the tab so the right ModulePageGuard fires (staff vs guests
 * permissions are distinct).
 *
 * Staff context routes to the unified person profile under the staff
 * module gate (`/location/:locationId/staff/:personId`). The former
 * parallel wallet page was retired — the profile is now state-driven and
 * renders the staff token wallet inline for staff-state persons.
 */
import { useParams } from 'react-router-dom';

export type PersonProfileTab = 'guest' | 'staff' | string;

export function usePersonProfilePath(): (personId: string, tab?: PersonProfileTab) => string {
  const { locationId } = useParams<{ locationId?: string }>();
  return (personId, tab) => {
    const isStaff = (tab ?? '').toLowerCase() === 'staff';
    if (locationId) {
      // Staff → the staff-gated unified profile route (the profile resolves
      // the staff state from data). Guests keep the guests-gated route.
      if (isStaff) {
        return `/location/${locationId}/staff/${personId}`;
      }
      const queryString = tab ? `?tab=${tab}` : '';
      return `/location/${locationId}/guests/${personId}${queryString}`;
    }
    const queryString = tab ? `?tab=${tab}` : '';
    return `/persons/${personId}${queryString}`;
  };
}
